import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { MapPin, Eye, Heart, TrendingUp, Plus, Check, Building2, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { VerifiedIcon } from './VerifiedBadge';
import { TrustBadge } from './TrustBadge';
import { useCountry } from '../context/CountryContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tiny inline social verification dots - appear on ListingCard when the seller
// has proven ownership of that account through the Get Verified flow.
// Order: Google → LinkedIn → Website (business), consistent across the app.
const SocialBadges = ({ seller, listingId }) => {
  if (!seller) return null;
  const items = [];
  if (seller.google_verified) {
    items.push({
      key: 'google',
      title: 'Google account verified',
      testid: `social-badge-google-${listingId}`,
      svg: (
        <svg viewBox="0 0 48 48" className="h-3 w-3" aria-hidden="true">
          <path fill="#EA4335" d="M24 9.5c3.9 0 6.6 1.7 8.1 3.1l6-5.8C34.4 3.2 29.7 1 24 1 14.8 1 6.9 6.3 3 14l7 5.4C11.9 13.7 17.4 9.5 24 9.5z"/>
          <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-2.8-.4-4.1H24v7.8h12.6c-.3 2.1-1.6 5.2-4.7 7.3l7.2 5.6c4.3-4 6.4-9.9 6.4-16.6z"/>
          <path fill="#FBBC05" d="M10 28.6c-.5-1.4-.8-2.9-.8-4.6s.3-3.2.8-4.6l-7-5.4C1.5 17.4.5 20.6.5 24s1 6.6 2.5 9.4l7-4.8z"/>
          <path fill="#34A853" d="M24 46.5c6.5 0 11.9-2.1 15.8-5.7l-7.2-5.6c-2 1.4-4.7 2.3-8.6 2.3-6.6 0-12.1-4.2-14-10.4l-7 5.4C6.9 39.7 14.8 46.5 24 46.5z"/>
        </svg>
      ),
    });
  }
  if (seller.linkedin_verified) {
    items.push({
      key: 'linkedin',
      title: 'LinkedIn account verified',
      testid: `social-badge-linkedin-${listingId}`,
      svg: (
        <svg viewBox="0 0 24 24" className="h-3 w-3" aria-hidden="true">
          <path fill="#0A66C2" d="M20.45 20.45h-3.55v-5.56c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.66H9.37V9h3.41v1.56h.05c.47-.9 1.63-1.85 3.36-1.85 3.59 0 4.26 2.36 4.26 5.43v6.31zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45zM22.23 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.46c.98 0 1.77-.77 1.77-1.72V1.72C24 .77 23.21 0 22.23 0z"/>
        </svg>
      ),
    });
  }
  if (seller.website_verified && seller.seller_type === 'business') {
    items.push({
      key: 'website',
      title: 'Business website verified',
      testid: `social-badge-website-${listingId}`,
      svg: <Globe className="h-3 w-3 text-blue-600" />,
    });
  }
  if (items.length === 0) return null;
  return (
    <span className="inline-flex items-center gap-0.5" data-testid={`social-badges-${listingId}`}>
      {items.map(item => (
        <span
          key={item.key}
          title={item.title}
          data-testid={item.testid}
          className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-white border border-gray-200 shadow-sm"
        >
          {item.svg}
        </span>
      ))}
    </span>
  );
};

const ListingCard = ({ 
  listing, 
  initialFavorite = false, 
  compact = false,
  showCompare = false,
  isCompareSelected = false,
  onCompareToggle
}) => {
  const navigate = useNavigate();
  const [isFavorite, setIsFavorite] = useState(initialFavorite);
  const { formatCurrency, formatMileage } = useCountry();

  // Always show price in the LISTING's country currency, regardless of visitor context.
  const listingCountry = (listing?.country || '').toUpperCase();
  const priceDisplay = (() => {
    if (listing?.price === null || listing?.price === undefined) return formatCurrency(listing?.price);
    if (listingCountry === 'LK' || listingCountry === 'SL') {
      return `Rs. ${Number(listing.price).toLocaleString()}`;
    }
    if (listingCountry === 'UK' || listingCountry === 'GB') {
      return `£${Number(listing.price).toLocaleString()}`;
    }
    return formatCurrency(listing.price);
  })();

  const handleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please login to add to favorites');
      navigate('/login');
      return;
    }

    try {
      if (isFavorite) {
        await axios.delete(`${API_URL}/api/user/favorites/${listing.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFavorite(false);
      } else {
        await axios.post(`${API_URL}/api/user/favorites/${listing.id}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFavorite(true);
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
    }
  };

  // Get the first image or use placeholder
  const imageUrl = listing.images && listing.images.length > 0 
    ? (listing.images[0].startsWith('http') ? listing.images[0] : `${API_URL}${listing.images[0]}`)
    : 'https://via.placeholder.com/400x300?text=No+Image';

  // Compact version for sidebar/grid display
  if (compact) {
    return (
      <Link to={`/listing/${listing.id}`} data-testid={`listing-card-${listing.id}`}>
        <Card className="group hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 overflow-hidden border hover:border-blue-300">
          <div className="relative">
            <img
              src={imageUrl}
              alt={listing.title}
              className="w-full h-36 object-cover group-hover:scale-105 transition-transform duration-200"
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/400x300?text=No+Image';
              }}
            />
            <Button
              size="icon"
              variant="secondary"
              className={`absolute top-1.5 right-1.5 rounded-full w-7 h-7 bg-white/90 hover:bg-white ${isFavorite ? 'text-red-500' : ''}`}
              onClick={handleFavorite}
            >
              <Heart className={`h-3.5 w-3.5 ${isFavorite ? 'fill-red-500' : ''}`} />
            </Button>
            {listing.featured && (
              <Badge className="absolute top-1.5 left-1.5 bg-gradient-to-r from-yellow-500 to-orange-500 text-white border-0 text-xs px-1.5 py-0.5">
                Featured
              </Badge>
            )}
          </div>
          <CardContent className="p-3">
            {/* Title with fixed height for 2 lines max, truncate with ellipsis */}
            <h3 
              className="font-medium text-gray-900 text-sm group-hover:text-blue-600 transition-colors mb-1 overflow-hidden"
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                minHeight: '2.5rem', /* Fixed height for 2 lines (1.25rem line-height * 2) */
                lineHeight: '1.25rem'
              }}
            >
              {listing.is_demo && (
                <span
                  className="inline-block align-middle mr-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-gray-200 text-gray-600 border border-gray-300"
                  data-testid={`demo-pill-compact-${listing.id}`}
                >
                  Demo
                </span>
              )}
              {listing.title}
            </h3>
            <div className="flex items-center justify-between mb-1">
              <span className="text-lg font-bold text-blue-600 notranslate" translate="no">
                {priceDisplay}
              </span>
            </div>
            <div className="flex items-center text-xs text-gray-500">
              <MapPin className="h-3 w-3 mr-1 notranslate" />
              <span className="truncate">{listing.location || 'N/A'}</span>
            </div>
            {/* Trust + Social badges (compact) */}
            {(listing.seller?.trust_tier && listing.seller.trust_tier !== 'UNVERIFIED') ||
              listing.seller?.google_verified ||
              listing.seller?.linkedin_verified ||
              (listing.seller?.website_verified && listing.seller?.seller_type === 'business') ? (
              <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                {listing.seller?.trust_tier && listing.seller.trust_tier !== 'UNVERIFIED' && (
                  <TrustBadge tier={listing.seller.trust_tier} score={listing.seller.trust_score} size="xs" />
                )}
                <SocialBadges seller={listing.seller} listingId={listing.id} />
              </div>
            ) : null}
          </CardContent>
        </Card>
      </Link>
    );
  }

  return (
    <Link to={`/listing/${listing.id}`}>
      <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden border-2 hover:border-blue-400">
        <div className="relative">
          <img
            src={imageUrl}
            alt={listing.title}
            className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/400x300?text=No+Image';
            }}
          />
          <Button
            size="icon"
            variant="secondary"
            className={`absolute top-2 right-2 rounded-full w-8 h-8 bg-white/90 hover:bg-white ${isFavorite ? 'text-red-500' : ''}`}
            onClick={handleFavorite}
          >
            <Heart className={`h-4 w-4 ${isFavorite ? 'fill-red-500' : ''}`} />
          </Button>
          {listing.featured && (
            <Badge className="absolute top-2 left-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white border-0">
              Featured
            </Badge>
          )}
          {listing.promoted && (
            <Badge className="absolute top-2 left-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white border-0 flex items-center space-x-1">
              <TrendingUp className="h-3 w-3" />
              <span>Promoted</span>
            </Badge>
          )}
        </div>
        <CardContent className="p-4">
          {/* Title with fixed height for 2 lines max, truncate with ellipsis */}
          <h3 
            className="font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors overflow-hidden"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              minHeight: '3rem', /* Fixed height for 2 lines (1.5rem line-height * 2) */
              lineHeight: '1.5rem'
            }}
          >
            {listing.is_demo && (
              <span
                className="inline-block align-middle mr-1.5 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-gray-200 text-gray-600 border border-gray-300"
                data-testid={`demo-pill-${listing.id}`}
              >
                Demo
              </span>
            )}
            {listing.title}
          </h3>
          <div className="flex items-center justify-between mb-3">
            <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent notranslate" translate="no">
              {priceDisplay}
            </span>
            {listing.negotiable && (
              <Badge variant="outline" className="text-xs">Negotiable</Badge>
            )}
          </div>
          <div className="flex items-center text-sm text-gray-500 mb-2">
            <MapPin className="h-4 w-4 mr-1 notranslate" />
            <span>{listing.location || 'N/A'}</span>
          </div>
          <div className="flex items-center justify-between text-xs text-gray-400">
            <div className="flex items-center gap-2">
              <div className="flex items-center">
                <Eye className="h-3 w-3 mr-1 notranslate" />
                <span className="notranslate" translate="no">{listing.views || 0} views</span>
              </div>
              {listing.seller?.verification_level && listing.seller.verification_level !== 'none' && (
                <VerifiedIcon level={listing.seller.verification_level} size={14} />
              )}
              {listing.seller?.trust_tier && listing.seller.trust_tier !== 'UNVERIFIED' && (
                <TrustBadge
                  tier={listing.seller.trust_tier}
                  score={listing.seller.trust_score}
                  size="xs"
                  showLabel
                />
              )}
              <SocialBadges seller={listing.seller} listingId={listing.id} />
              {listing.seller?.seller_type === 'business' && listing.seller?.company_registration_verified && (
                <Badge
                  variant="outline"
                  className="text-[10px] px-1.5 py-0.5 border-blue-400 text-blue-700 bg-blue-50 gap-1"
                  title={listing.seller.business_name ? `Verified UK business: ${listing.seller.business_name}${listing.seller.director_verified ? ' • Director confirmed' : ''}` : 'Verified UK business'}
                  data-testid={`business-badge-${listing.id}`}
                >
                  <Building2 className="h-3 w-3" />
                  <span className="truncate max-w-[110px]">
                    {listing.seller.business_name || 'Verified Business'}
                  </span>
                  {listing.seller.director_verified && (
                    <Check className="h-3 w-3 text-emerald-600" />
                  )}
                </Badge>
              )}
            </div>
            <span>{listing.condition}</span>
          </div>
          
          {/* Compare Button - Only show for property listings */}
          {showCompare && onCompareToggle && (
            <div className="mt-3 pt-3 border-t">
              <Button
                variant={isCompareSelected ? "default" : "outline"}
                size="sm"
                className={`w-full h-8 text-xs ${isCompareSelected ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onCompareToggle(listing.id);
                }}
                data-testid={`compare-btn-${listing.id}`}
              >
                {isCompareSelected ? (
                  <>
                    <Check className="w-3 h-3 mr-1" />
                    Selected for Compare
                  </>
                ) : (
                  <>
                    <Plus className="w-3 h-3 mr-1" />
                    Compare
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
};

export default ListingCard;
